/**
 * Common Translations Module (English)
 * 
 * Contains: Navigation, Footer, Settings, UI Elements, Tooltips, Common Utilities
 * 
 * @module common
 * @language en
 */

export const common = {
  nav: {
    // 1. Overview (standalone)
    overview: "Overview",
    
    // 2. Youth Futures (dropdown parent)
    youthFutures: "Youth Futures",
    // Youth Futures dropdown items
    impactMetrics: "Impact & Metrics",
    mentalHealth: "Mental Health Dimension",
    workforceEducation: "Workforce & Education",
    entrepreneurshipSupport: "Entrepreneurship Dimension",
    
    // 3. Database (dropdown parent)
    database: "Database",
    // Database dropdown items
    memberInsights: "Members Insights",
    programsEvents: "Programs & Events",
    technologyFuture: "Technology & Future",
    regionalAnalysis: "Regional Analysis",
    
    // 4. Vision 2040 (standalone)
    vision2040: "Vision 2040",
    
    // 5. Know More (dropdown parent)
    knowMore: "Learn More",
    // Know More dropdown items
    methodology: "Methodology",
    contributing: "Contributing",
    faq: "FAQ",
    
    // Additional nav items
    backToTop: "Back to top",
    brandName: "JCI Vision Pulse",
    observatoryTitle: "Youth Futures Observatory",
    developmentCouncils: "Development Councils"
  },
  footer: {
    copyright: "© 2025 JCI Development Councils. Licensed under CC BY-NC-SA.",
    methodology: "Methodology based on data from 147,424 JCI members globally.",
    poweredBy: "Powered by",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service"
  },
  settings: {
    title: "Settings",
    subtitle: "Manage your preferences and customize your experience.",
    appearance: "Appearance",
    appearanceDescription: "Customize how the observatory looks on your device.",
    light: "Light",
    dark: "Dark",
    system: "System",
    language: "Language",
    languageDescription: "Select your preferred language for the interface.",
    notifications: "Notifications",
    notificationsDescription: "Configure how you want to receive updates and alerts.",
    emailNotifications: "Email Notifications",
    emailNotificationsDescription: "Receive updates and reports via email.",
    pushNotifications: "Push Notifications",
    pushNotificationsDescription: "Get real-time alerts in your browser.",
    productUpdates: "Product Updates",
    productUpdatesDescription: "Stay informed about new features and improvements.",
    preferencesUpdated: "Preferences updated",
    notificationsEnabled: "enabled",
    notificationsDisabled: "disabled",
    themeUpdated: "Theme updated",
    themeChangedTo: "Theme changed to",
    languageUpdated: "Language updated",
    languageChangedTo: "Language changed to",
    profile: "Your Profile",
    profileDescription: "Your account information",
    clickToChangeAvatar: "Click avatar to change",
    avatar: {
      error: "Error",
      loginRequired: "You must be logged in to upload an avatar",
      invalidType: "Invalid file type",
      selectImage: "Please select an image file",
      tooLarge: "Image too large",
      selectSmaller: "Please select a smaller image (max 100KB after compression)",
      updated: "Avatar updated",
      updatedSuccess: "Your profile picture has been updated successfully",
      uploadFailed: "Upload failed",
      failedToUpload: "Failed to upload avatar"
    },
    observatoryLens: "Observatory Lens",
    observatoryLensDescription: "Your personalized data focus preferences",
    primaryInterest: "Primary Interest",
    focusCountry: "Focus Country",
    refineLens: "Refine Observatory Lens",
    signInPrompt: "Sign in for more features",
    signInPromptDescription: "Save your preferences and access member-only content",
    translationDebug: "Translation Debugger",
    translationDebugDescription: "View missing translation keys and completion status",
    // Danger Zone - GDPR Account Deletion
    dangerZone: "Danger Zone",
    dangerZoneDescription: "Irreversible actions that affect your account",
    deleteAccount: "Delete Account",
    deleteAccountDescription: "Permanently delete your account and all associated data",
    deleteAccountConfirmTitle: "Delete your account?",
    deleteAccountConfirmDescription: "This action cannot be undone. All your data will be permanently deleted including:",
    deleteAccountDataProfile: "Your profile information",
    deleteAccountDataNotifications: "Notification preferences and history",
    deleteAccountDataAvatar: "Your profile picture",
    deleteAccountDataProgress: "Observatory progress and achievements",
    typeEmailConfirm: "Type your email to confirm:",
    deleteAccountButton: "Delete My Account Permanently",
    accountDeleted: "Account Deleted",
    accountDeletedDescription: "Your account and all associated data have been permanently deleted.",
    deleteFailed: "Deletion Failed",
    deleteFailedDescription: "Could not delete your account. Please try again or contact support.",
    emailMismatch: "Email does not match",
    // Privacy & Data Export
    privacyData: "Privacy & Data",
    privacyDataDescription: "Manage your personal data and privacy settings",
    downloadData: "Download Your Data",
    downloadDataDescription: "Export all your personal data in JSON format (profile, notifications, preferences)",
    downloadAsJson: "Download as JSON",
    downloading: "Preparing download...",
    downloadComplete: "Download Complete",
    downloadCompleteDescription: "Your personal data has been downloaded successfully.",
    downloadFailed: "Download Failed",
    downloadFailedDescription: "Could not export your data. Please try again.",
    cancel: "Cancel",
    // Data Processing Record - GDPR Article 30
    processingRecord: {
      title: "Data Processing Record",
      activitiesRecorded: "processing activities recorded",
      view: "View Record",
      hide: "Hide Record",
      noActivities: "No processing activities recorded yet",
      legalBasisLabel: "Legal Basis",
      showMore: "Show {{count}} more",
      showLess: "Show less",
      gdprNote: "Under GDPR Article 30, you have the right to access records of all data processing activities on your account.",
      activityTypes: {
        accountCreated: "Account created",
        profileUpdated: "Profile information updated",
        onboardingCompleted: "Onboarding completed",
        achievementUnlocked: "Achievement unlocked",
        preferencesCreated: "Notification preferences set",
        preferencesUpdated: "Notification preferences updated",
        notificationSent: "Notification sent",
        notificationRead: "Notification read",
        roleAssigned: "Role assigned"
      },
      legalBasis: {
        consent: "Consent",
        contractPerformance: "Contract performance",
        legitimateInterest: "Legitimate interest"
      }
    }
  },
  progress: {
    title: "Observatory Mastery",
    description: "Track your journey through the Observatory",
    emailVerified: "Email verified",
    visitedModules: "Visited",
    modules: "modules",
    setupComplete: "Setup complete",
    congratulations: "Congratulations! You've reached Visionary status!"
  },
  notifications: {
    title: "Notifications",
    empty: "No notifications yet",
    viewSettings: "Notification Settings",
    markAllRead: "Mark all read",
    preferences: "Notification Preferences",
    preferencesDescription: "Control how you receive intel briefings",
    emailAlerts: "Email Alerts",
    emailAlertsDescription: "Receive Strategic Intel Briefings via email",
    inAppAlerts: "In-App Alerts",
    inAppAlertsDescription: "Show toast notifications when you login",
    weeklyDigest: "Weekly Digest",
    weeklyDigestDescription: "Receive a summary of intel every week",
    updateFailed: "Failed to update preferences",
    intelBriefing: "📊 New Intel Available",
    deleteAll: "Delete All",
    deleteAllConfirm: "Delete All Notifications?",
    deleteAllDescription: "This will permanently remove all your notification history. This action cannot be undone.",
    deleteAllButton: "Delete All Notifications",
    deleted: "Notifications deleted",
    deletedDescription: "All your notifications have been permanently removed.",
    deleteError: "Delete failed",
    deleteErrorDescription: "Could not delete notifications. Please try again.",
    cancel: "Cancel",
    deletedSingle: "Notification deleted",
    swipeHint: "Swipe left to delete"
  },
  admin: {
    sendBriefing: "Send Intel Briefing",
    sendBriefingDescription: "Send a Strategic Intel Briefing to subscribed users",
    module: "Module",
    selectModule: "Select a module",
    briefingTitle: "Title",
    titlePlaceholder: "e.g., New Mental Health Data Released",
    summary: "Summary",
    summaryPlaceholder: "Brief description of the new intel...",
    sendToSubscribers: "Send to Subscribers",
    recipientNote: "Recipients are users with matching primary interest who have email alerts enabled",
    fillAllFields: "Please fill in all fields",
    briefingSent: "Briefing Sent",
    sentTo: "Sent to",
    subscribers: "subscribers",
    sendFailed: "Failed to send briefing",
    briefingSentTo: "Briefing sent to",
    users: "users",
    sending: "Sending...",
    // API Key Management
    apiKeys: {
      title: "API Keys",
      description: "Manage API keys for external software access to YFI data",
      create: "Create API Key",
      createTitle: "Create New API Key",
      createDescription: "Create a new API key for external software to access the YFI data API.",
      name: "Name",
      namePlaceholder: "e.g., My Application",
      keyDescription: "Description",
      descriptionPlaceholder: "What will this key be used for?",
      expiration: "Expiration",
      never: "Never expires",
      days30: "30 days",
      days90: "90 days",
      months6: "6 months",
      year1: "1 year",
      cancel: "Cancel",
      creating: "Creating...",
      created: "API Key Created",
      createdDescription: "Your new API key has been created. Copy it now - it won't be shown again.",
      saveKeyWarning: "Save your API key now!",
      saveKeyDescription: "This is the only time you'll see this key. Copy it and store it securely.",
      savedKey: "I've saved my key",
      revoke: "Revoke",
      revokeTitle: "Revoke API Key",
      revokeDescription: "Are you sure you want to revoke this key? This action cannot be undone. Any applications using this key will immediately lose access.",
      revoking: "Revoking...",
      revoked: "API Key Revoked",
      revokedDescription: "The API key has been revoked and can no longer be used.",
      noKeys: "No API keys yet",
      noKeysDescription: "Create your first API key to get started.",
      active: "Active",
      expired: "Expired",
      created_at: "Created",
      lastUsed: "Last used",
      requests: "requests",
      viewDocs: "View the API Integration Guide for documentation on using these keys.",
      failedToLoad: "Failed to load API keys",
      failedToCreate: "Failed to create API key",
      failedToRevoke: "Failed to revoke API key"
    },
    // Data Administration
    dataAdmin: {
      title: "Data Administration",
      description: "Monitor and manage observatory data",
      refresh: "Refresh",
      coverage: "Country Profile Coverage",
      coverageDescription: "of countries have detailed profiles",
      databaseTables: "Database Tables",
      yfiCountries: "YFI Countries",
      yfiCountriesDesc: "Countries with YFI scores",
      countryProfiles: "Country Profiles",
      countryProfilesDesc: "Detailed country data",
      jciStats: "JCI Statistics",
      jciStatsDesc: "Global organizational metrics",
      regions: "Regions",
      regionsDesc: "JCI regions",
      activeApiKeys: "Active API Keys",
      activeApiKeysDesc: "External software access",
      apiRequests: "API Requests",
      apiRequestsDesc: "Total logged requests",
      countryProfilesTitle: "Country Profiles",
      published: "published",
      draft: "Draft",
      updated: "Updated",
      noProfiles: "No country profiles created yet",
      quickActions: "Quick Actions",
      viewYfiRankings: "View YFI Rankings",
      regionalAnalysis: "Regional Analysis",
      backendNote: "For advanced database management, access the Lovable Cloud backend panel from the project settings."
    }
  },
  featuredInsight: {
    title: "Your Focus Country",
    fiiScore: "Youth Futures Index",
    globalAverage: "Global Average",
    viewFullRankings: "View Full Index",
    unlockTitle: "Unlock Your Personalized Insights",
    unlockDescription: "Complete setup to see how your focus country compares to the global average across all YFI dimensions."
  },
  roles: {
    admin: "Administrator",
    administrator: "Administrator",
    moderator: "Moderator",
    user: "User",
  },
  interests: {
    "mental health": "Mental Health",
    "mental-health": "Mental Health",
    "workforce preparation": "Workforce Preparation",
    "workforce-preparation": "Workforce Preparation",
    "entrepreneurship": "Entrepreneurship",
    "technology adoption": "Technology Adoption",
    "technology-adoption": "Technology Adoption",
    "regional analysis": "Regional Analysis",
    "regional-analysis": "Regional Analysis",
  },
  mastery: {
    observer: "Observer",
    analyst: "Analyst",
    visionary: "Visionary",
  },
  visibility: {
    membersOnly: "MEMBERS ONLY",
    adminOnly: "ADMIN ONLY",
    hiddenAdminOnly: "HIDDEN (admin only)",
  },
  header: {
    title: "JCI Development Councils Youth Futures Observatory",
    subtitle: "Development Councils Global Observatory",
    tagline: "Developing Leaders for a Changing World"
  },
  common: {
    jan: "Jan",
    feb: "Feb",
    mar: "Mar",
    apr: "Apr",
    may: "May",
    jun: "Jun",
    jul: "Jul",
    aug: "Aug",
    sep: "Sep",
    oct: "Oct",
    nov: "Nov",
    dec: "Dec",
    preferredProgramTypes: "Preferred Program Types",
    preferredEventFormat: "Preferred Event Format",
    regionalPreferences: "Regional Preferences",
    inPerson: "In-Person",
    online: "Online",
    hybrid: "Hybrid",
    networking: "Networking",
    development: "Development",
    leadership: "Leadership",
    search: "Search",
    settings: "Settings",
    signIn: "Sign In",
    signOut: "Sign Out",
    toggleMenu: "Toggle navigation menu",
    scrollToTop: "Scroll to top",
    source: "Source",
    sources: {
      membership2025: "JCI Membership 2025",
      impactReport2025: "JCI Impact Report 2025"
    },
    loading: "Loading...",
    noDataAvailable: "No data available",
    noDataDescription: "Data is being loaded or is not available at the moment.",
    errorLoadingChart: "Error loading chart",
    months: {
      jan: "Jan",
      feb: "Feb",
      mar: "Mar",
      apr: "Apr",
      may: "May",
      jun: "Jun",
      jul: "Jul",
      aug: "Aug",
      sep: "Sep",
      oct: "Oct",
      nov: "Nov",
      dec: "Dec"
    }
  },
  ui: {
    notFound: {
      title: "404",
      message: "Oops! Page not found",
      returnHome: "Return to Home"
    },
    keyboardShortcuts: {
      title: "Keyboard Shortcuts",
      toggleDarkMode: "Toggle dark/light mode",
      cycleLanguages: "Cycle through languages",
      openSearch: "Open search",
      showShortcuts: "Show keyboard shortcuts",
      ctrlOrCmd: "Use Ctrl on Windows/Linux or ⌘ on Mac"
    },
    commandPalette: {
      searchPlaceholder: "Search pages...",
      noResults: "No results found.",
      pagesHeading: "Pages"
    },
    languageToggle: {
      selectLanguage: "Select language",
      changeLanguage: "Change language"
    }
  },
  charts: {
    noDataAvailable: "No data available",
    dataLoadingSoon: "Data will be loaded soon",
    noFunnelData: "No funnel data available",
    configureProcessData: "Configure the process data",
    radarUnavailable: "Radar data unavailable",
    configurePerformanceIndicators: "Configure performance indicators",
    performanceUnavailable: "Performance data unavailable",
    configureTrendData: "Configure trend data",
    currentProgress: "Current Progress",
    goal2040: "Goal 2040",
    mainValue: "Main Value",
    trend: "Trend",
    goal: "Goal",
    // Chart axis & tooltip labels
    fiiScore: "YFI Score",
    score: "Score",
    value: "Value",
    percentage: "Percentage",
    region: "Region",
    country: "Country",
    members: "Members",
    year: "Year",
    month: "Month",
    count: "Count",
    total: "Total",
    current: "Current",
    projected: "Projected",
    average: "Average"
  },
  metrics: {
    membersActive: "Active Members",
    countriesPresent: "Countries Present",
    startupsSupported: "Startups Supported",
    sustainableProjects: "Sustainable Projects",
    completed: "completed",
    countries: "Countries",
    members: "Members",
    projects: "Projects",
    projectsPerYear: "Projects/year",
    memberDensity: "Member Density",
    country: "Country",
    regionalDistribution: "Regional Distribution",
    globalPresence: "Global JCI presence in 2025",
    activeMembers: "Active Members",
    globally: "globally",
    worldwidePresence: "worldwide presence",
    communities: "Communities",
    activeCommunities: "active communities",
    yearsOfHistory: "Years of History",
    since1915: "since 1915",
    vs2024: "vs 2024",
    annualProjects: "Annual Projects",
    in2025: "in 2025"
  },
  index: {
    heroTitle: "JCI Development Councils Youth Futures Observatory",
    heroSubtitle: "Measuring Youth Leadership, Mental Health & Economic Opportunity Globally",
    heroDescription: "A data-driven platform analyzing the conditions for young leaders across 114 countries. Using verified metrics from JCI's global network and research partnerships.",
    globalImpactTitle: "Global Impact at a Glance",
    verifiedData: "Verified data from JCI Impact Report 2025",
    globalMembers: "Global Members",
    activeWorldwide: "Active worldwide",
    projects: "Projects",
    period20242025: "Period: 2024-2025",
    volunteerHours: "Volunteer Hours",
    hoursContributed: "Hours contributed",
    countries: "Countries",
    worldwidePresence: "Worldwide presence",
    localOrganizations: "Local Organizations",
    activeCommunities: "Active communities",
    digitalReachTitle: "Digital Reach & Footprint",
    totalFollowers: "Total Followers",
    jciccUsers: "jci.cc Users",
    memberDemographicsTitle: "Member Demographics Snapshot",
    averageAge: "Average Age",
    yearsOld: "Years old",
    universityEducated: "University Educated",
    bachelorsOrHigher: "Bachelor's degree or higher",
    fullTime: "Full-time",
    entrepreneurs: "Entrepreneurs",
    employmentStatus: "Employment Status"
  },
  globalPresenceMap: {
    title: "JCI Global Presence",
    subtitle: "Active in 114 Countries across 4 Regions",
    countriesLabel: "countries",
    regionsLabel: "regions",
    countriesCount: "Countries",
    membersCount: "Members",
    localOrgs: "Local Orgs",
    regions: {
      asiaPacific: "Asia and the Pacific",
      europe: "Europe",
      africaMe: "Africa and the Middle East",
      americas: "America"
    }
  },
  mentalHealth: {
    serviceCoverageGap: "30-40% service coverage gap between US/Canada and Latin America",
    criticalGap: "Critical gap: only 10-30% service coverage vs 70%+ in Europe",
    onlyTrained: "(only {percentage}% trained)"
  },
  memberInsights: {
    alumniInsight: "53% new members (1-3 yrs) bring fresh energy while 24% veterans (7+ yrs) provide core leadership and stability"
  },
  onboarding: {
    commandCenterSetup: "Command Center Setup",
    stepOf: "Step {{current}} of {{total}}",
    continue: "Continue",
    back: "Back",
    completeSetup: "Complete Setup",
    preferencesUpdated: "Preferences Updated",
    lensUpdated: "Your observatory lens has been updated.",
    error: "Error",
    saveFailed: "Failed to save your preferences. Please try again.",
    identity: {
      title: "Confirm Your Identity",
      description: "Let us know who you are and which JCI organization you belong to.",
      fullName: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      nationalOrg: "JCI National Organization",
      nationalOrgPlaceholder: "Select your national organization...",
      searchCountry: "Search country...",
      noCountryFound: "No country found.",
      localOrg: "JCI Local Organization",
      optional: "Optional",
      localOrgPlaceholder: "e.g., JCI Tokyo, JCI Manila Bay"
    },
    lens: {
      title: "Choose Your Lens",
      description: "Select your primary area of interest. This will personalize your dashboard experience.",
      mentalHealth: "Mental Health",
      mentalHealthDesc: "Focus on psychological well-being, resilience, and mental wellness initiatives.",
      education: "Education & Workforce",
      educationDesc: "Explore learning outcomes, skills development, and employment trends.",
      entrepreneurship: "Entrepreneurship",
      entrepreneurshipDesc: "Discover business ecosystem health, startup activity, and innovation metrics."
    },
    benchmark: {
      title: "Set Your Benchmark",
      description: "Choose a focus country to compare against the Global YFI Average throughout the dashboard.",
      focusCountry: "Focus Country",
      focusCountryPlaceholder: "Select your focus country...",
      searchCountry: "Search country...",
      noCountryFound: "No country found.",
      vsGlobal: "{{country}} vs Global Average",
      globalAverage: "Global Average",
      aboveAverage: "+{{value}} above global average",
      belowAverage: "-{{value}} below global average"
    },
    config: {
      ready: "Ready to Launch!",
      configuring: "Configuring Your Command Center",
      step1: "Analyzing your preferences...",
      step2: "Configuring dashboard layout...",
      step3: "Loading personalized insights...",
      step4: "Preparing your Command Center..."
    }
  },
  fiiClassifications: {
    excellent: "Excellent",
    good: "Good",
    moderate: "Moderate",
    deficient: "Deficient",
    critical: "Critical"
  },
  regions: {
    asiaPacific: "Asia and the Pacific",
    europe: "Europe",
    americas: "America",
    africaMiddleEast: "Africa and the Middle East"
  },
  access: {
    exclusiveMemberInsight: "Exclusive Member Insight",
    adminOnlyModule: "Admin-only Module",
    joinCTA: "Join the JCI Development Councils Youth Futures Observatory to unlock the full database, insights and projections.",
    adminOnlyMessage: "This {{moduleLabel}} content is only available to administrators.",
    joinNowLogin: "Join Now / Login",
    backToDashboard: "Back to Dashboard",
    memberAccess: "Member Access",
    passwordlessEntry: "Passwordless entry with Magic Link or Google.",
    joinObservatory: "Join the Observatory",
    unlockDescription: "Unlock member-only insights, projections, and interactive modules.",
    email: "Email",
    emailPlaceholder: "you@company.com",
    sendMagicLink: "Send Magic Link",
    continueWithGoogle: "Continue with Google",
    authDisclaimer: "By continuing you agree to receive authentication emails.",
    signingIn: "Signing you in...",
    checkInbox: "Check your inbox",
    magicLinkSent: "We sent you a secure magic link to sign in.",
    invalidEmail: "Invalid email",
    invalidEmailDescription: "Please enter a valid email address.",
    loginFailed: "Login failed",
    googleLoginFailed: "Google login failed"
  },
  chatJCI: {
    title: "ChatJCI",
    openChat: "Open chat",
    closeChat: "Close chat",
    placeholder: "Type your question...",
    send: "Send message",
    thinking: "Thinking...",
    greeting: "Hello, {{name}}! How can I help you today?",
    defaultName: "there",
    fallbackMessage: "I'm sorry, I couldn't find that information in my knowledge base.",
    errorMessage: "Sorry, something went wrong. Please try again.",
    rateLimitMessage: "Too many requests. Please wait a moment before trying again.",
    authRequired: "Please sign in to use ChatJCI.",
    welcomeSubtitle: "Ask me anything about JCI programs, membership, or Vision 2040",
    newChat: "New Chat",
    poweredBy: "Powered by Google Gemini",
    suggestedPrompts: {
      title: "Try asking about:",
      vision2040: "What is JCI Vision 2040?",
      risePrograms: "Tell me about JCI RISE programs",
      membership: "How can I become a JCI member?",
      councils: "What are the Development Councils?",
      youthIndex: "Explain the Youth Futures Index"
    },
    loginRequired: {
      title: "Sign in to Chat",
      description: "ChatJCI is available exclusively for JCI members. Sign in to start a conversation.",
      action: "Sign In"
    },
    locked: {
      title: "Members-Only AI Assistant",
      description: "ChatJCI is your personal AI guide to JCI programs, Vision 2040, and the Youth Futures Index. Sign in to start a conversation."
    },
    sidebar: {
      newChat: "New Chat",
      today: "Today",
      yesterday: "Yesterday",
      last7Days: "Last 7 Days",
      older: "Older",
      noConversations: "No conversations yet",
      searchPlaceholder: "Search conversations...",
      noResults: "No conversations found",
      deleteConversation: "Delete conversation?",
      deleteConfirmation: "This will permanently delete this conversation and all its messages.",
      delete: "Delete",
      renameConversation: "Rename",
      exportAll: "Export All Conversations"
    },
    export: {
      asJson: "Export as JSON",
      asMarkdown: "Export as Markdown",
      downloading: "Preparing download...",
      success: "Conversations exported",
      singleSuccess: "Conversation exported",
      successDescription: "Your chat history has been downloaded successfully",
      error: "Export failed",
      errorDescription: "Unable to export conversations. Please try again.",
      noConversations: "No conversations to export"
    },
    copy: {
      tooltip: "Copy to clipboard",
      success: "Conversation copied",
      successDescription: "Markdown copied to clipboard. Paste anywhere!",
      error: "Copy failed",
      errorDescription: "Unable to copy to clipboard. Please try again.",
      noMessages: "No messages to copy"
    },
    share: {
      button: "Share",
      dialogTitle: "Share Conversation",
      dialogDescription: "Anyone with this link can view this conversation.",
      createLink: "Create Share Link",
      generating: "Generating link...",
      created: "Share link created",
      createdDescription: "Your conversation is now shareable",
      linkCopied: "Link copied to clipboard",
      copyError: "Failed to copy link",
      revokeShare: "Revoke Share",
      revoked: "Share revoked",
      revokedDescription: "This link will no longer work",
      revokeError: "Failed to revoke share",
      createError: "Failed to create share",
      createErrorDescription: "Unable to create share link. Please try again.",
      publicNote: "Anyone with the link can view this conversation",
      viewCount: "{{count}} views",
      views: "views",
      expiresAt: "Expires on {{date}}",
      expirationLabel: "Link expires in",
      days7: "7 days",
      days30: "30 days",
      days90: "90 days"
    },
    sharedView: {
      title: "Shared ChatJCI Conversation",
      sharedBy: "Shared by JCI Member",
      sharedOn: "Shared on {{date}}",
      signUpCta: "Want your own AI assistant?",
      signUpButton: "Sign up for ChatJCI",
      notFound: "Conversation not found",
      notFoundDescription: "This shared link may have expired or been revoked.",
      expired: "This shared link has expired"
    },
    message: {
      copy: "Copy",
      copied: "Copied!",
      regenerate: "Regenerate",
      edit: "Edit",
      stopGenerating: "Stop",
      editPlaceholder: "Edit your message...",
      cancel: "Cancel",
      saveAndSubmit: "Save & Submit"
    },
    streaming: {
      generating: "Generating...",
      stopped: "Stopped"
    }
  },
  errorBoundary: {
    title: "Something went wrong",
    description: "We apologize for the inconvenience. An unexpected error has occurred.",
    tryAgain: "Try Again",
    goHome: "Go Home",
    errorDetails: "Error Details (Development Only)",
    helpText: "If this problem persists, please refresh the page or contact support.",
    chunkLoadError: {
      title: "Failed to Load Page",
      description: "The page could not be loaded. This may be due to a network issue or outdated cache.",
      retry: "Try Again",
      retryMessage: "Attempt {{current}} of {{max}}",
      maxRetries: "Maximum retry attempts reached."
    }
  },
  gdpr: {
    cookieTitle: "Cookie Preferences",
    cookieDescription: "We use cookies to analyze site usage and improve your experience. Your data is never sold to third parties.",
    learnMore: "Learn more about our privacy practices",
    accept: "Accept",
    decline: "Decline",
    analyticsTitle: "Analytics & Cookies",
    analyticsDescription: "Manage how we use cookies and analytics to improve your experience",
    allowAnalytics: "Allow Analytics",
    allowAnalyticsDescription: "Help us improve by sharing anonymous usage data",
    analyticsEnabled: "Analytics Enabled",
    analyticsEnabledDescription: "You can now help us improve the platform with anonymous usage data.",
    analyticsDisabled: "Analytics Disabled",
    analyticsDisabledDescription: "Your usage data will no longer be collected.",
    whatWeCollect: "What we collect:",
    collectItem1: "Page views and navigation patterns",
    collectItem2: "Feature engagement and scroll depth",
    collectItem3: "Anonymous device and browser info",
    neverCollect: "We never collect personal information or sell your data to third parties."
  },
  apiDocs: {
    badge: "Developer API",
    hero: {
      title: "Youth Futures Observatory API",
      subtitle: "Access comprehensive youth development data from 114 countries. Build applications, create visualizations, and integrate YFI metrics into your projects.",
      getAccess: "Get API Access",
      viewDocs: "View OpenAPI Spec"
    },
    stats: {
      countries: "Countries",
      statistics: "Statistics",
      endpoints: "Endpoints"
    },
    features: {
      badge: "Why Use Our API",
      title: "Built for Developers",
      subtitle: "Our API provides reliable, well-documented access to comprehensive youth development metrics. Whether you're building research tools, dashboards, or data visualizations — we've got you covered.",
      sampleRequest: "Sample Request",
      restful: {
        title: "RESTful JSON API",
        description: "Standard REST endpoints returning clean, predictable JSON responses. Easy to integrate with any programming language or platform."
      },
      filtering: {
        title: "Comprehensive Filtering",
        description: "Filter data by region, classification, score ranges, and more. Get exactly the data you need with powerful query parameters.",
        highlight: "15+ Filters"
      },
      pagination: {
        title: "Pagination & Sorting",
        description: "Handle large datasets efficiently with built-in pagination. Sort by any field in ascending or descending order.",
        highlight: "Offset-based"
      },
      auth: {
        title: "Secure API Keys",
        description: "SHA-256 hashed keys with per-key rate limits. Generate, revoke, and monitor keys from your dashboard."
      },
      logging: {
        title: "Request Logging",
        description: "Full analytics on your API usage. Track request counts, response times, and error rates in real-time.",
        highlight: "Analytics"
      },
      cors: {
        title: "CORS Enabled",
        description: "Cross-origin requests supported out of the box. Build browser-based applications without proxy servers."
      }
    },
    auth: {
      badge: "Security",
      title: "Authentication",
      subtitle: "All API requests require authentication via API key. Follow these simple steps to get started.",
      step1: {
        title: "Get Your API Key",
        description: "Navigate to Settings and generate a new API key. Give it a descriptive name for easy management."
      },
      step2: {
        title: "Add to Headers",
        description: "Include your API key in the X-API-Key header with every request you make to our endpoints."
      },
      step3: {
        title: "Start Building",
        description: "You're ready! Make requests to any endpoint and start integrating YFI data into your application."
      },
      headerExample: "Header Format"
    },
    endpoints: {
      badge: "API Reference",
      title: "Available Endpoints",
      subtitle: "Three powerful endpoints to access all YFI data. Each endpoint supports filtering, sorting, and pagination.",
      parameters: "Query Parameters",
      sampleRequest: "Sample Request",
      sampleResponse: "Sample Response",
      yfi: {
        title: "YFI Rankings",
        description: "Access Youth Futures Index scores and rankings for all countries. Filter by region, classification, sort by any score dimension, and paginate results."
      },
      countries: {
        title: "Country Profiles",
        description: "Detailed country-level data including demographics, JCI presence, mental health indicators, workforce data, and entrepreneurship metrics."
      },
      stats: {
        title: "Global Statistics",
        description: "Aggregate JCI statistics including membership counts, regional breakdowns, organizational metrics, and historical data."
      }
    },
    params: {
      yfi: {
        region: "Filter by JCI region: amdec, adc, apdc, edc",
        classification: "Filter by YFI tier: Excellent, Good, Moderate, Deficient, Critical",
        code: "Get specific country by ISO 2-letter code (e.g., BR, DE, JP)",
        countryCodes: "Filter multiple countries (comma-separated, e.g., br,de,jp,us)",
        fields: "Select specific fields to return (sparse fieldset, e.g., country_code,yfi_score)",
        sort: "Sort field: yfi_score, mental_health_score, workforce_score, entrepreneurship_score, country_name",
        order: "Sort direction: asc (ascending) or desc (descending)",
        limit: "Results per page (default: 100, max: 1000)",
        offset: "Pagination offset for fetching subsequent pages"
      },
      countries: {
        slug: "Country profile identifier (e.g., brazil, germany)"
      },
      stats: {
        key: "Get specific statistic by its unique key identifier",
        category: "Filter by category: membership, regional, organizational"
      },
      indicators: {
        key: "Get specific indicator: YFI_SCORE, YFI_MENTAL, YFI_WORKFORCE, YFI_ENTREPRENEURSHIP",
        category: "Filter by category: composite, mental_health, workforce, entrepreneurship",
        lang: "Language for localized content: en, pt, es, fr, ja"
      },
      benchmarks: {
        region: "Get benchmarks for specific region: amdec, adc, apdc, edc",
        indicator: "Indicator to benchmark: yfi_score, mental_health_score, workforce_score, entrepreneurship_score",
        top: "Number of top performers to include (default: 5, max: 20)"
      }
    },
    table: {
      parameter: "Parameter",
      type: "Type",
      description: "Description"
    },
    responseFormat: {
      title: "Response Format",
      subtitle: "All endpoints return a consistent JSON structure with data and metadata.",
      structure: "Standard Response Structure",
      success: {
        title: "Data Array",
        description: "Results are returned in the 'data' field as an array (or object for single-item requests)."
      },
      meta: {
        title: "Metadata Included",
        description: "Every response includes pagination info, request ID for debugging, and timestamp."
      }
    },
    errors: {
      title: "Error Handling",
      subtitle: "The API uses standard HTTP status codes. Here are the most common error responses you may encounter.",
      "401": {
        title: "Unauthorized",
        description: "API key is missing, invalid, or has been revoked. Check your X-API-Key header."
      },
      "404": {
        title: "Not Found",
        description: "The requested resource doesn't exist. Verify the endpoint path and query parameters."
      },
      "405": {
        title: "Method Not Allowed",
        description: "Only GET requests are supported. Ensure you're using the correct HTTP method."
      },
      "429": {
        title: "Rate Limited",
        description: "You've exceeded your rate limit. Wait for the reset period or upgrade your key limits."
      },
      "500": {
        title: "Server Error",
        description: "An unexpected error occurred. Contact support with your request_id for assistance."
      }
    },
    useCases: {
      title: "Use Cases",
      subtitle: "See how organizations are using YFI data to drive impact.",
      research: {
        title: "Academic Research",
        description: "Universities and research institutions use YFI data for comparative studies on youth development."
      },
      journalism: {
        title: "Data Journalism",
        description: "News organizations create compelling visualizations and stories about global youth trends."
      },
      ngo: {
        title: "NGO Planning",
        description: "Non-profits use regional insights to plan interventions and allocate resources effectively."
      },
      policy: {
        title: "Policy Analysis",
        description: "Government agencies analyze YFI metrics to inform youth-focused policy decisions."
      }
    },
    quickStart: {
      title: "Quick Start",
      subtitle: "Make your first API request in seconds. Copy this cURL command and replace with your API key."
    },
    copy: "Copy",
    copied: "Copied!",
    codeCopied: "Code copied to clipboard",
    copyFailed: "Failed to copy code",
    cta: {
      title: "Ready to Build?",
      subtitle: "Generate your API key and start building with YFI data today. No credit card required.",
      button: "Get Your API Key"
    }
  }
} as const;
